import { Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Label, MessageBar, MessageBarBody, Option, Spinner, Text } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../AppContext";
import { Module } from "../../models/module";
import { RoleDTO } from "../../models/role";
import { CopyUserGroupsResult } from "../../models/userGroup";
import { copyUserGroups, getUserGroupsByModule, getUserGroupsByModuleAndOrganizationID } from "../../services/userGroupService";

type Props = {
  open: boolean;
  onClose: () => void;
  onCopied: () => void;
};

const CopyUserGroupsDialog = (props: Props) => {
  const { user } = useAppContext();

  const [sourceModuleId, setSourceModuleId] = useState<string>("");
  const [targetModuleId, setTargetModuleId] = useState<string>("");
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [loadingGroups, setLoadingGroups] = useState<boolean>(false);
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<CopyUserGroupsResult | null>(null);

  // General users can copy between any ramavtal, everyone else only between ramavtal they administer
  const modules: Module[] = useMemo(() => {
    if (!user) return [];
    if (user.roles.some((r: RoleDTO) => r.roleType === 'General')) {
      return user.modules.filter((m: Module) => m.moduleType === 'Tendering');
    }
    const administered: Module[] = user.assignments
      .filter((a: any) => a.role.roleName === 'Ramavtalsansvarig' || a.role.roleName === 'Kontoadmin')
      .map((a: any) => a.module);
    return administered.filter((m, i) => administered.findIndex(x => x.moduleID === m.moduleID) === i);
  }, [user]);

  useEffect(() => {
    if (props.open) {
      setSourceModuleId("");
      setTargetModuleId("");
      setGroups([]);
      setSelectedGroupIds([]);
      setErrorMessage(null);
      setResult(null);
    }
  }, [props.open]);

  const handleSourceSelect = async (moduleId: string) => {
    setSourceModuleId(moduleId);
    if (targetModuleId === moduleId) setTargetModuleId("");
    setSelectedGroupIds([]);
    setGroups([]);
    setErrorMessage(null);
    try {
      setLoadingGroups(true);
      const isGeneral = user.roles.some((r: RoleDTO) => r.roleType === 'General');
      const result = isGeneral
        ? await getUserGroupsByModule(+moduleId)
        : await getUserGroupsByModuleAndOrganizationID(+moduleId, user.organization.organizationID);
      setGroups(result);
    } catch (error) {
      console.error('Error fetching user groups:', error);
      setErrorMessage('Grupperna kunde inte hämtas.');
    } finally {
      setLoadingGroups(false);
    }
  };

  const allSelected = groups.length > 0 && selectedGroupIds.length === groups.length;

  const toggleGroup = (groupId: number, checked: boolean) => {
    setSelectedGroupIds(prev => checked ? [...prev, groupId] : prev.filter(id => id !== groupId));
  };

  const handleCopy = async () => {
    setIsCopying(true);
    setErrorMessage(null);
    try {
      const response = await copyUserGroups({
        SourceModuleID: +sourceModuleId,
        TargetModuleID: +targetModuleId,
        UserGroupIDs: selectedGroupIds
      });
      setResult(response);
    } catch (error: any) {
      console.error('Error copying user groups:', error);
      setErrorMessage(error?.response?.data?.message ?? 'Grupperna kunde inte kopieras.');
    } finally {
      setIsCopying(false);
    }
  };

  const handleClose = () => {
    if (result && result.created.length > 0) props.onCopied();
    props.onClose();
  };

  const targetModules = modules.filter(m => m.moduleID.toString() !== sourceModuleId);

  return (
    <Dialog
      open={props.open}
      onOpenChange={(_event, data) => !data.open && handleClose()}
      modalType="alert"
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                shape="circular"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={handleClose}
              />
            }
          >
            Kopiera grupper
          </DialogTitle>
          <DialogContent>
            {errorMessage && (
              <MessageBar intent="error" className="mb-3">
                <MessageBarBody>{errorMessage}</MessageBarBody>
              </MessageBar>
            )}
            {result ? (
              <div className="flex flex-col gap-3">
                <MessageBar intent={result.created.length > 0 ? "success" : "warning"}>
                  <MessageBarBody>
                    {result.created.length} {result.created.length === 1 ? 'grupp kopierades' : 'grupper kopierades'}
                    {result.skipped.length > 0 && `, ${result.skipped.length} hoppades över`}.
                  </MessageBarBody>
                </MessageBar>
                {result.skipped.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <Text weight="semibold">Hoppades över:</Text>
                    {result.skipped.map(s => (
                      <Text key={s.userGroupID} size={200}>
                        {s.userGroupName ?? `Grupp ${s.userGroupID}`}: {s.reason}
                      </Text>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <Field label="Kopiera från ramavtal:" required>
                  <Dropdown
                    placeholder="Välj ramavtal"
                    value={modules.find(m => m.moduleID.toString() === sourceModuleId)?.moduleName ?? ""}
                    onOptionSelect={(_e, data) => data.optionValue && handleSourceSelect(data.optionValue)}
                    positioning={{ autoSize: false }}
                    listbox={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
                  >
                    {modules.map(m => (
                      <Option key={m.moduleID} value={m.moduleID.toString()}>{m.moduleName}</Option>
                    ))}
                  </Dropdown>
                </Field>

                {/* Not wrapped in a Field: Checkbox is a field control, so inside a Field every checkbox
                    would share the Field's id and clicking any label would toggle the first one. */}
                {sourceModuleId && (
                  <div className="flex flex-col">
                    <Label required>Grupper att kopiera:</Label>
                    {loadingGroups ? (
                      <Spinner size="small" />
                    ) : groups.length === 0 ? (
                      <Text size={200}>Inga grupper hittades i det valda ramavtalet.</Text>
                    ) : (
                      <div className="flex flex-col">
                        <Checkbox
                          label="Markera alla"
                          checked={allSelected}
                          onChange={(_e, data) => setSelectedGroupIds(data.checked ? groups.map(g => g.userGroupID) : [])}
                        />
                        {groups.map(g => (
                          <Checkbox
                            key={g.userGroupID}
                            label={`${g.userGroupName} (${g.userGroupAssignments?.length ?? 0} ${(g.userGroupAssignments?.length ?? 0) === 1 ? 'medlem' : 'medlemmar'})`}
                            checked={selectedGroupIds.includes(g.userGroupID)}
                            onChange={(_e, data) => toggleGroup(g.userGroupID, data.checked as boolean)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <Field label="Kopiera till ramavtal:" required>
                  <Dropdown
                    placeholder="Välj ramavtal"
                    disabled={!sourceModuleId}
                    value={targetModules.find(m => m.moduleID.toString() === targetModuleId)?.moduleName ?? ""}
                    onOptionSelect={(_e, data) => setTargetModuleId(data.optionValue ?? "")}
                    positioning={{ autoSize: false }}
                    listbox={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
                  >
                    {targetModules.map(m => (
                      <Option key={m.moduleID} value={m.moduleID.toString()}>{m.moduleName}</Option>
                    ))}
                  </Dropdown>
                </Field>

                <Text size={200}>
                  Medlemmar och deras roller i gruppen kopieras. Placeringar, väntande inbjudningar och ansökningar kopieras inte.
                  Grupper med samma namn som redan finns i målramavtalet hoppas över.
                </Text>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {result ? (
              <Button appearance="primary" onClick={handleClose}>Stäng</Button>
            ) : (
              <>
                <Button onClick={handleClose} disabled={isCopying}>Avbryt</Button>
                <Button
                  appearance="primary"
                  onClick={handleCopy}
                  disabled={isCopying || !sourceModuleId || !targetModuleId || selectedGroupIds.length === 0}
                >
                  {isCopying ? "Kopierar..." : "Kopiera"}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CopyUserGroupsDialog;
